import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabaseClient';

interface RemunerationDossier {
  id: string;
  restaurant_name: string;
  ville: string;
  ao_user_id: string;
  ao_name: string;
  ao_email: string;
  mois: string;
  ca_mensuel: number;
  pct_plateforme: number;
  pct_commission_tp13: number;
  taux_ao: number;
  commission_tp13_calculee: number;
  remuneration_ao_calculee: number;
  override_admin: number | null;
  statut_paiement: 'A_PAYER' | 'PAYE' | 'EN_ATTENTE';
  notes_admin: string | null;
  created_at: string;
  updated_at: string;
}

interface AOProfile {
  id: string;
  full_name: string;
  email: string;
}

export default function RemunerationPage() {
  const { isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [dossiers, setDossiers] = useState<RemunerationDossier[]>([]);
  const [aoList, setAoList] = useState<AOProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successToast, setSuccessToast] = useState<string | null>(null);
  const [filterAO, setFilterAO] = useState('');
  const [filterStatut, setFilterStatut] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    restaurant_name: '',
    ville: '',
    ao_user_id: '',
    mois: '',
    ca_mensuel: 0,
    pct_plateforme: 33,
    pct_commission_tp13: 0,
    taux_ao: 10,
    override_admin: null as number | null,
    statut_paiement: 'A_PAYER' as 'A_PAYER' | 'PAYE' | 'EN_ATTENTE',
    notes_admin: '',
  });

  useEffect(() => {
    if (!authLoading && !isAdmin) {
      navigate('/dashboard');
    }
  }, [isAdmin, authLoading, navigate]);

  useEffect(() => {
    if (isAdmin) {
      loadData();
    }
  }, [isAdmin]);

  useEffect(() => {
    if (successToast) {
      const timer = setTimeout(() => setSuccessToast(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [successToast]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Fonction de calcul des montants
  const calculateAmounts = (
    ca: number,
    pctPlateforme: number,
    pctCommissionTP13: number,
    tauxAO: number
  ) => {
    const commissionTP13 = ca * (pctPlateforme / 100) * (pctCommissionTP13 / 100);
    const remunerationAO = commissionTP13 * (tauxAO / 100);
    return {
      commission_tp13_calculee: Math.round(commissionTP13 * 100) / 100,
      remuneration_ao_calculee: Math.round(remunerationAO * 100) / 100,
    };
  };

  const loadData = async () => {
    setLoading(true);
    try {
      // Charger les AO
      const { data: aoData, error: aoError } = await supabase
        .from('profiles')
        .select('user_id, full_name, email')
        .eq('role', 'AO')
        .order('full_name', { ascending: true });

      if (aoError) throw aoError;

      setAoList((aoData || []).map((ao: any) => ({
        id: ao.user_id,
        full_name: ao.full_name || ao.email,
        email: ao.email,
      })));

      // Charger les dossiers
      const { data: dossiersData, error: dossiersError } = await supabase
        .from('crm_remuneration_ao')
        .select('*')
        .order('mois', { ascending: false });

      if (dossiersError) throw dossiersError;

      // Récupérer les user_ids uniques des AO
      const aoUserIds = [...new Set((dossiersData || []).map((d: any) => d.ao_user_id).filter(Boolean))];
      
      let aoProfilesMap = new Map();
      if (aoUserIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('user_id, full_name, email')
          .in('user_id', aoUserIds);
        
        aoProfilesMap = new Map((profiles || []).map((p: any) => [p.user_id, p]));
      }

      // Mapper les dossiers avec recalcul si nécessaire
      setDossiers((dossiersData || []).map((d: any) => {
        const aoProfile = aoProfilesMap.get(d.ao_user_id);
        
        // Recalculer les montants si absents ou incohérents
        let commission = d.commission_tp13_calculee;
        let remuneration = d.remuneration_ao_calculee;
        
        if (!commission || !remuneration) {
          const calculated = calculateAmounts(
            d.ca_mensuel,
            d.pct_plateforme,
            d.pct_commission_tp13,
            d.taux_ao
          );
          commission = calculated.commission_tp13_calculee;
          remuneration = calculated.remuneration_ao_calculee;
        }

        return {
          ...d,
          commission_tp13_calculee: commission,
          remuneration_ao_calculee: remuneration,
          ao_name: aoProfile?.full_name || 'N/A',
          ao_email: aoProfile?.email || '',
        };
      }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setFormData({
      restaurant_name: '',
      ville: '',
      ao_user_id: '',
      mois: '',
      ca_mensuel: 0,
      pct_plateforme: 33,
      pct_commission_tp13: 0,
      taux_ao: 10,
      override_admin: null,
      statut_paiement: 'A_PAYER',
      notes_admin: '',
    });
    setShowModal(true);
  };

  const openEdit = (dossier: RemunerationDossier) => {
    setEditingId(dossier.id);
    setFormData({
      restaurant_name: dossier.restaurant_name,
      ville: dossier.ville,
      ao_user_id: dossier.ao_user_id,
      mois: dossier.mois,
      ca_mensuel: dossier.ca_mensuel,
      pct_plateforme: dossier.pct_plateforme,
      pct_commission_tp13: dossier.pct_commission_tp13,
      taux_ao: dossier.taux_ao,
      override_admin: dossier.override_admin,
      statut_paiement: dossier.statut_paiement,
      notes_admin: dossier.notes_admin || '',
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
  };

  const handleSave = async () => {
    if (!formData.ao_user_id || !formData.restaurant_name.trim() || !formData.mois) {
      setError('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setSaving(true);
    try {
      // Calculer les montants
      const calculated = calculateAmounts(
        formData.ca_mensuel,
        formData.pct_plateforme,
        formData.pct_commission_tp13,
        formData.taux_ao
      );

      const payload = {
        restaurant_name: formData.restaurant_name.trim(),
        ville: formData.ville.trim() || null,
        ao_user_id: formData.ao_user_id,
        mois: formData.mois,
        ca_mensuel: formData.ca_mensuel,
        pct_plateforme: formData.pct_plateforme,
        pct_commission_tp13: formData.pct_commission_tp13,
        taux_ao: formData.taux_ao,
        commission_tp13_calculee: calculated.commission_tp13_calculee,
        remuneration_ao_calculee: calculated.remuneration_ao_calculee,
        override_admin: formData.override_admin,
        statut_paiement: formData.statut_paiement,
        notes_admin: formData.notes_admin.trim() || null,
      };

      if (editingId) {
        const { error } = await supabase
          .from('crm_remuneration_ao')
          .update(payload)
          .eq('id', editingId);

        if (error) throw error;
        setSuccessToast('Dossier mis à jour');
      } else {
        const { error } = await supabase
          .from('crm_remuneration_ao')
          .insert([payload]);

        if (error) throw error;
        setSuccessToast('Dossier créé');
      }

      closeModal();
      await loadData();
    } catch (err: any) {
      setError(err?.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Supprimer ce dossier ?')) return;

    try {
      const { error } = await supabase
        .from('crm_remuneration_ao')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setSuccessToast('Dossier supprimé');
      await loadData();
    } catch (err: any) {
      setError(err?.message || 'Erreur lors de la suppression');
    }
  };

  const exportCSV = () => {
    const headers = [
      'Restaurant',
      'Ville',
      'AO',
      'Email AO',
      'Mois',
      'CA mensuel',
      '% Plateforme',
      '% Commission TP13',
      'Taux AO',
      'Commission TP13',
      'Rémunération AO',
      'Override admin',
      'Rémunération finale',
      'Statut paiement',
      'Notes admin',
      'Date création',
    ];

    const rows = filteredDossiers.map(d => {
      const remunerationFinale = d.override_admin ?? d.remuneration_ao_calculee;
      return [
        d.restaurant_name,
        d.ville || '',
        d.ao_name,
        d.ao_email,
        new Date(d.mois).toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' }),
        d.ca_mensuel,
        d.pct_plateforme,
        d.pct_commission_tp13,
        d.taux_ao,
        d.commission_tp13_calculee,
        d.remuneration_ao_calculee,
        d.override_admin || '',
        remunerationFinale,
        d.statut_paiement === 'PAYE' ? 'Payé' : d.statut_paiement === 'A_PAYER' ? 'À payer' : 'En attente',
        d.notes_admin || '',
        new Date(d.created_at).toLocaleDateString('fr-FR'),
      ];
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `remuneration_ao_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const filteredDossiers = dossiers.filter(d => {
    if (filterAO && d.ao_user_id !== filterAO) return false;
    if (filterStatut && d.statut_paiement !== filterStatut) return false;
    return true;
  });

  // KPIs
  const totalCA = filteredDossiers.reduce((sum, d) => sum + d.ca_mensuel, 0);
  const totalCommissionTP13 = filteredDossiers.reduce((sum, d) => sum + d.commission_tp13_calculee, 0);
  const totalAPayerAO = filteredDossiers
    .filter(d => d.statut_paiement === 'A_PAYER')
    .reduce((sum, d) => sum + (d.override_admin ?? d.remuneration_ao_calculee), 0);
  const totalPayeAO = filteredDossiers
    .filter(d => d.statut_paiement === 'PAYE')
    .reduce((sum, d) => sum + (d.override_admin ?? d.remuneration_ao_calculee), 0);

  if (authLoading || !isAdmin) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#0a0a0a]">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-[#DC143C] border-t-transparent rounded-full animate-spin"></div>
          <p className="text-sm text-gray-400">Vérification des permissions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 bg-[#0a0a0a] min-h-screen">
      {/* Toasts */}
      {successToast && (
        <div className="fixed top-4 right-4 z-50 px-4 py-3 bg-green-600 text-white rounded-lg shadow-lg text-sm font-medium">
          <i className="ri-checkbox-circle-line mr-2"></i>
          {successToast}
        </div>
      )}
      {error && (
        <div className="fixed top-4 right-4 z-50 px-4 py-3 bg-red-600 text-white rounded-lg shadow-lg text-sm font-medium">
          <i className="ri-error-warning-line mr-2"></i>
          {error}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">CRM Rémunération AO</h1>
          <p className="text-gray-400">Gestion des commissions et paiements</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={loadData}
            className="px-4 py-2 bg-[#1a1a1a] hover:bg-[#232838] text-white rounded-lg transition-colors text-sm whitespace-nowrap cursor-pointer"
          >
            <i className="ri-refresh-line mr-2"></i>Actualiser
          </button>
          <button
            onClick={exportCSV}
            className="px-4 py-2 bg-[#1a1a1a] hover:bg-[#232838] text-white rounded-lg transition-colors text-sm whitespace-nowrap cursor-pointer"
          >
            <i className="ri-download-line mr-2"></i>Export CSV
          </button>
          <button
            onClick={openCreate}
            className="px-4 py-2 bg-[#DC143C] hover:bg-[#B01030] text-white rounded-lg transition-colors text-sm font-medium whitespace-nowrap cursor-pointer"
          >
            <i className="ri-add-line mr-2"></i>Nouveau dossier
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-[#1a1a1a] border border-gray-800 rounded-lg p-4">
          <p className="text-sm text-gray-400 mb-1">Total CA saisi</p>
          <p className="text-2xl font-bold text-white">{totalCA.toLocaleString('fr-FR')} €</p>
        </div>
        <div className="bg-[#1a1a1a] border border-gray-800 rounded-lg p-4">
          <p className="text-sm text-gray-400 mb-1">Total commission TP13</p>
          <p className="text-2xl font-bold text-green-400">{totalCommissionTP13.toLocaleString('fr-FR')} €</p>
        </div>
        <div className="bg-[#1a1a1a] border border-gray-800 rounded-lg p-4">
          <p className="text-sm text-gray-400 mb-1">Total à payer AO</p>
          <p className="text-2xl font-bold text-yellow-400">{totalAPayerAO.toLocaleString('fr-FR')} €</p>
        </div>
        <div className="bg-[#1a1a1a] border border-gray-800 rounded-lg p-4">
          <p className="text-sm text-gray-400 mb-1">Total déjà payé</p>
          <p className="text-2xl font-bold text-blue-400">{totalPayeAO.toLocaleString('fr-FR')} €</p>
        </div>
      </div>

      {/* Filtres */}
      <div className="flex items-center gap-3 flex-wrap mb-6">
        <select
          value={filterAO}
          onChange={e => setFilterAO(e.target.value)}
          className="px-3 py-2 bg-[#1a1a1a] border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-[#DC143C] cursor-pointer"
        >
          <option value="">Tous les AO</option>
          {aoList.map(ao => (
            <option key={ao.id} value={ao.id}>{ao.full_name}</option>
          ))}
        </select>
        <select
          value={filterStatut}
          onChange={e => setFilterStatut(e.target.value)}
          className="px-3 py-2 bg-[#1a1a1a] border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-[#DC143C] cursor-pointer"
        >
          <option value="">Tous les statuts</option>
          <option value="A_PAYER">À payer</option>
          <option value="PAYE">Payé</option>
          <option value="EN_ATTENTE">En attente</option>
        </select>
        <span className="text-sm text-gray-500 ml-auto">{filteredDossiers.length} résultat{filteredDossiers.length > 1 ? 's' : ''}</span>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="flex flex-col items-center gap-3">
            <div className="w-10 h-10 border-4 border-[#DC143C] border-t-transparent rounded-full animate-spin"></div>
            <p className="text-sm text-gray-400">Chargement...</p>
          </div>
        </div>
      ) : (
        <div className="bg-[#1a1a1a] rounded-lg border border-gray-800 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1200px]">
              <thead className="bg-[#0a0a0a] border-b border-gray-800">
                <tr>
                  {['Restaurant', 'Ville', 'AO', 'Mois', 'CA mensuel', '% Plateforme', '% TP13', 'Taux AO', 'Commission TP13', 'Rémunération AO', 'Override', 'Rémunération finale', 'Statut', 'Actions'].map(h => (
                    <th key={h} className="px-3 py-3 text-left text-xs font-semibold text-gray-400 uppercase whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {filteredDossiers.map(d => {
                  const remunerationFinale = d.override_admin ?? d.remuneration_ao_calculee;
                  return (
                    <tr key={d.id} className="hover:bg-[#0a0a0a] transition-colors">
                      <td className="px-3 py-3 text-sm text-white whitespace-nowrap">{d.restaurant_name}</td>
                      <td className="px-3 py-3 text-sm text-gray-400 whitespace-nowrap">{d.ville || '—'}</td>
                      <td className="px-3 py-3 text-sm text-gray-300 whitespace-nowrap">{d.ao_name}</td>
                      <td className="px-3 py-3 text-sm text-gray-400 whitespace-nowrap">
                        {new Date(d.mois).toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' })}
                      </td>
                      <td className="px-3 py-3 text-sm text-white font-semibold whitespace-nowrap">{d.ca_mensuel.toLocaleString('fr-FR')} €</td>
                      <td className="px-3 py-3 text-sm text-gray-400 whitespace-nowrap">{d.pct_plateforme}%</td>
                      <td className="px-3 py-3 text-sm text-gray-400 whitespace-nowrap">{d.pct_commission_tp13}%</td>
                      <td className="px-3 py-3 text-sm text-gray-400 whitespace-nowrap">{d.taux_ao}%</td>
                      <td className="px-3 py-3 text-sm text-green-400 font-semibold whitespace-nowrap">{d.commission_tp13_calculee.toLocaleString('fr-FR')} €</td>
                      <td className="px-3 py-3 text-sm text-blue-400 font-semibold whitespace-nowrap">{d.remuneration_ao_calculee.toLocaleString('fr-FR')} €</td>
                      <td className="px-3 py-3 text-sm text-purple-400 whitespace-nowrap">{d.override_admin ? `${d.override_admin.toLocaleString('fr-FR')} €` : '—'}</td>
                      <td className="px-3 py-3 text-sm text-[#DC143C] font-bold whitespace-nowrap">{remunerationFinale.toLocaleString('fr-FR')} €</td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          d.statut_paiement === 'PAYE' ? 'bg-green-500/15 text-green-400 border border-green-500/30' :
                          d.statut_paiement === 'A_PAYER' ? 'bg-yellow-500/15 text-yellow-400 border border-yellow-500/30' :
                          'bg-gray-500/15 text-gray-400 border border-gray-500/30'
                        }`}>
                          {d.statut_paiement === 'PAYE' ? 'Payé' : d.statut_paiement === 'A_PAYER' ? 'À payer' : 'En attente'}
                        </span>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => openEdit(d)}
                            className="w-7 h-7 flex items-center justify-center rounded bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white transition-colors cursor-pointer"
                          >
                            <i className="ri-edit-line text-xs"></i>
                          </button>
                          <button
                            onClick={() => handleDelete(d.id)}
                            className="w-7 h-7 flex items-center justify-center rounded bg-gray-700 hover:bg-red-600/30 text-gray-300 hover:text-red-400 transition-colors cursor-pointer"
                          >
                            <i className="ri-delete-bin-line text-xs"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {filteredDossiers.length === 0 && (
              <div className="text-center py-16">
                <i className="ri-folder-open-line text-4xl text-gray-600"></i>
                <p className="text-gray-500 mt-2">Aucun dossier trouvé</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="bg-[#1a1a1a] border border-gray-700 rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-800">
              <h2 className="text-xl font-bold text-white">
                {editingId ? 'Modifier le dossier' : 'Nouveau dossier'}
              </h2>
              <button onClick={closeModal} className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white transition-colors cursor-pointer">
                <i className="ri-close-line"></i>
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Restaurant <span className="text-red-400">*</span></label>
                  <input
                    type="text"
                    value={formData.restaurant_name}
                    onChange={e => setFormData(f => ({ ...f, restaurant_name: e.target.value }))}
                    className="w-full px-3 py-2 bg-[#0a0a0a] border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-[#DC143C]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Ville</label>
                  <input
                    type="text"
                    value={formData.ville}
                    onChange={e => setFormData(f => ({ ...f, ville: e.target.value }))}
                    className="w-full px-3 py-2 bg-[#0a0a0a] border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-[#DC143C]"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">AO <span className="text-red-400">*</span></label>
                  <select
                    value={formData.ao_user_id}
                    onChange={e => setFormData(f => ({ ...f, ao_user_id: e.target.value }))}
                    className="w-full px-3 py-2 bg-[#0a0a0a] border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-[#DC143C] cursor-pointer"
                  >
                    <option value="">— Sélectionner un AO —</option>
                    {aoList.map(ao => (
                      <option key={ao.id} value={ao.id}>{ao.full_name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Mois <span className="text-red-400">*</span></label>
                  <input
                    type="month"
                    value={formData.mois}
                    onChange={e => setFormData(f => ({ ...f, mois: e.target.value }))}
                    className="w-full px-3 py-2 bg-[#0a0a0a] border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-[#DC143C]"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">CA mensuel (€)</label>
                  <input
                    type="number"
                    value={formData.ca_mensuel}
                    onChange={e => setFormData(f => ({ ...f, ca_mensuel: parseFloat(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 bg-[#0a0a0a] border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-[#DC143C]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">% Plateforme</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.pct_plateforme}
                    onChange={e => setFormData(f => ({ ...f, pct_plateforme: parseFloat(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 bg-[#0a0a0a] border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-[#DC143C]"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">% Commission TP13</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.pct_commission_tp13}
                    onChange={e => setFormData(f => ({ ...f, pct_commission_tp13: parseFloat(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 bg-[#0a0a0a] border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-[#DC143C]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Taux AO (%)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.taux_ao}
                    onChange={e => setFormData(f => ({ ...f, taux_ao: parseFloat(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 bg-[#0a0a0a] border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-[#DC143C]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Override admin (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.override_admin || ''}
                    onChange={e => setFormData(f => ({ ...f, override_admin: e.target.value ? parseFloat(e.target.value) : null }))}
                    className="w-full px-3 py-2 bg-[#0a0a0a] border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-[#DC143C]"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Statut paiement</label>
                <select
                  value={formData.statut_paiement}
                  onChange={e => setFormData(f => ({ ...f, statut_paiement: e.target.value as any }))}
                  className="w-full px-3 py-2 bg-[#0a0a0a] border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-[#DC143C] cursor-pointer"
                >
                  <option value="A_PAYER">À payer</option>
                  <option value="PAYE">Payé</option>
                  <option value="EN_ATTENTE">En attente</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Notes admin</label>
                <textarea
                  value={formData.notes_admin}
                  onChange={e => setFormData(f => ({ ...f, notes_admin: e.target.value }))}
                  rows={3}
                  maxLength={500}
                  className="w-full px-3 py-2 bg-[#0a0a0a] border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-[#DC143C] resize-none"
                />
                <p className="text-xs text-gray-500 mt-1">{formData.notes_admin.length}/500 caractères</p>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-800">
              <button
                onClick={closeModal}
                className="px-5 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-lg text-sm transition-colors whitespace-nowrap cursor-pointer"
              >
                Annuler
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-5 py-2.5 bg-[#DC143C] hover:bg-[#B01030] disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors whitespace-nowrap flex items-center gap-2 cursor-pointer"
              >
                {saving && <i className="ri-loader-4-line animate-spin"></i>}
                {editingId ? 'Enregistrer' : 'Créer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}